// bf-ingredient-glossary.js — Glossary tooltip injector, v2 (A11Y-001, session 7)
//
// Marks ingredient names in product copy and blog articles as glossary terms and gives each one a
// Tippy pop-up with the ingredient's details. Data: window.GLOSSARY, built by
// bf-ingredient-grid-filters.js from the Storefront API; this script waits for it (bounded).
//
// v2 makes the terms accessible (WCAG 2.1.1, 4.1.2, 1.4.13, 2.4.7):
//  - each term is a real <button> — reachable by Tab, operable with Enter/Space, named by its text;
//  - Tippy sets aria-expanded on the button (false on create, true while open) and points
//    aria-describedby at the pop-up while it is open;
//  - the pop-up is appended beside its term (its own wrapper span), so Tab moves from the term into
//    the pop-up's "More Info" link and out again, instead of the pop-up living at the end of <body>;
//  - Enter or Space toggles it; Escape closes it and focus comes back to the term; focus leaving the
//    pop-up closes it; hovering the pop-up keeps it open; one pop-up is open at a time;
//  - the info icon is aria-hidden so the button's name is just the ingredient.
// Tippy 6.3.7 and Popper 2.11.8 are vendored (bf-tippy.min.js, bf-popper.min.js) and loaded deferred
// before this file in layout/theme.liquid.

(function () {
  'use strict';

  var POLL_MS = 100;
  var POLL_LIMIT = 150; // ~15 s, then give up quietly: no data means no tooltips, not a poll for ever
  var polls = 0;

  // layout/theme.liquid loads the glossary only on pages whose markup holds one of these (perf, session 24):
  // a container added here must be added to its list too.
  var CONTAINERS = [
    '[id^="deets-track"] ul',
    '.collapsible-content ul',
    '.blog-layout__main .tooltip-friendly'
  ].join(',');

  function initGlossaryWhenReady() {
    if (window.GLOSSARY) return initGlossary();
    if (polls++ < POLL_LIMIT) setTimeout(initGlossaryWhenReady, POLL_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGlossaryWhenReady);
  } else {
    initGlossaryWhenReady();
  }
  document.addEventListener('shopify:section:load', initGlossaryWhenReady);

  // ── Terms ─────────────────────────────────────────────────────────────────────────────────────

  function termRegex() {
    var pattern = Object.keys(window.GLOSSARY)
      .map(function (t) { return t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/-/g, '[-\\s]'); })
      .join('|');
    return new RegExp('\\b(' + pattern + ')\\b', 'gi');
  }

  function buildTerm(match) {
    var key = match.trim().toLowerCase().replace(/\s+/g, '-');
    var wrap = document.createElement('span');
    wrap.className = 'bf-term-wrap';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'bf-term';
    btn.dataset.term = key;
    btn.textContent = match; // Tippy owns aria-expanded: it sets it on create and toggles it — only if the
                             // button does not already carry one, so it is deliberately not set here
    var icon = document.createElement('span');
    icon.className = 'bf-term-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = 'ⓘ';
    btn.appendChild(icon);
    wrap.appendChild(btn);
    return wrap;
  }

  // Replace each matching run of text with a term button, keeping the surrounding text as text nodes
  // (never through innerHTML, so an ampersand or angle bracket in the copy stays literal).
  function wrapTerms(container, regex) {
    var walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    var nodes = [];
    while (walker.nextNode()) {
      var n = walker.currentNode;
      if (!n.parentNode.closest('.bf-term-wrap, a, button, script, style')) nodes.push(n);
    }
    nodes.forEach(function (node) {
      var text = node.textContent;
      regex.lastIndex = 0;
      if (!regex.test(text)) return;
      var frag = document.createDocumentFragment();
      var last = 0, m;
      regex.lastIndex = 0;
      while ((m = regex.exec(text))) {
        if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)));
        frag.appendChild(buildTerm(m[0]));
        last = m.index + m[0].length;
      }
      if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
      node.parentNode.replaceChild(frag, node);
    });
  }

  // ── Pop-up content ────────────────────────────────────────────────────────────────────────────

  function joinList(v) { return Array.isArray(v) ? v.join(' • ') : (v || ''); }

  function buildContent(ref) {
    var d = window.GLOSSARY[ref.dataset.term] || {};
    var aroma = d.aroma_family || '';
    var chip = d.aroma_color ? '<span class="bf-chip" style="background:' + d.aroma_color + ';"></span>' : '';
    var perks = joinList(d.perks);
    var purpose = joinList(d.purpose);
    var aromatherapy = joinList(d.aromatherapy);
    var row = function (label, value) {
      return value
        ? '<p class="bf-tip-extra"><span class="label">' + label + '</span><span>' + value + '</span></p>'
        : '';
    };
    return '<div class="bf-tip">' +
      (d.img ? '<img src="' + d.img + '" alt="" class="bf-tip-thumb">' : '') +
      '<h6 class="bf-tip-title">' + (d.title || '') + '</h6>' +
      (d.latin ? '<p class="bf-tip-latin"><em>' + d.latin + '</em></p>' : '') +
      '<p class="bf-tip-desc">' + (d.desc || '') + '</p>' +
      '<hr/>' +
      row('Family:', aroma ? aroma + chip : '') +
      row('On Skin:', perks) +
      row('Aroma:', aromatherapy) +
      row('Role:', purpose) +
      (d.url ? '<a href="' + d.url + '" target="_blank" rel="noopener" class="bf-tip-link">🔗 More Info<span class="visually-hidden"> (opens in a new tab)</span></a>' : '') + // A11Y-012: the new tab is announced
      '</div>';
  }

  // ── Keyboard behaviour ────────────────────────────────────────────────────────────────────────
  // Tippy handles hover (mouseenter/leave), focus (show on focus, hide on blur) and pointer clicks;
  // keyboard activation and dismissal are handled here, deterministically:
  //  - Enter or Space on a term toggles its pop-up. A button turns those keys into a click — with
  //    `detail` 0 in Chrome and Firefox, and in any case right after the key's keydown on the term — and
  //    that click is caught in the capture phase and stopped before Tippy's pointer-oriented toggling can
  //    see it. Assistive technology's "activate" arrives the same way.
  //  - Escape closes the pop-up. If focus was inside it (on "More Info"), focus returns to the term — and
  //    that programmatic focus must not reopen the pop-up, so the next show is skipped once (this works
  //    only from the top-level onShow prop: Tippy ignores a plugin hook's return value).
  //  - Tippy only watches focus leaving the term, so focus leaving the pop-up (Tab past "More Info") is
  //    watched here and closes it.
  //  - One pop-up at a time: opening one closes any other.

  // A keyboard click is one with detail 0 (Chrome, Firefox) — or one that follows Enter/Space keydown on
  // the term (belt and braces for engines that report a simulated click's detail as 1).
  function onTermKeyDown(e, instance) {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') instance._bfKeyActivation = true;
  }
  function onTermClick(e, instance) {
    var fromKeyboard = e.detail === 0 || instance._bfKeyActivation === true;
    instance._bfKeyActivation = false;
    if (!fromKeyboard) return;         // a pointer click: Tippy's own toggling handles it
    e.stopImmediatePropagation();      // keyboard (or AT) activation: ours alone
    if (instance.state.isVisible) instance.hide(); else instance.show();
  }
  function onDocumentKeyDown(e, instance) {
    if (e.key !== 'Escape') return;
    var focusWasInside = instance.popper.contains(document.activeElement);
    instance.hide();
    if (focusWasInside) {
      instance._bfSkipNextShow = true;
      instance.reference.focus();
    }
  }
  function onPopperFocusOut(e, instance) {
    var to = e.relatedTarget; // null when focus goes nowhere (a click inside the pop-up): leave it open
    if (to && to !== instance.reference && !instance.popper.contains(to)) instance.hide();
  }

  var hooks = {
    onCreate: function (instance) {
      instance.reference.addEventListener('keydown', function (e) { onTermKeyDown(e, instance); }, true);
      instance.reference.addEventListener('click', function (e) { onTermClick(e, instance); }, true);
      instance.popper.addEventListener('focusout', function (e) { onPopperFocusOut(e, instance); });
      instance._bfOnDocumentKeyDown = function (e) { onDocumentKeyDown(e, instance); };
    },
    onShow: function (instance) {
      if (instance._bfSkipNextShow) { instance._bfSkipNextShow = false; return false; }
      tippy.hideAll({ exclude: instance });
      document.addEventListener('keydown', instance._bfOnDocumentKeyDown);
    },
    onHide: function (instance) {
      document.removeEventListener('keydown', instance._bfOnDocumentKeyDown);
    }
  };

  // ── Init ──────────────────────────────────────────────────────────────────────────────────────

  function initGlossary() {
    if (!window.GLOSSARY || !Object.keys(window.GLOSSARY).length) return;

    var regex = termRegex();
    document.querySelectorAll(CONTAINERS).forEach(function (container) {
      if (container.closest('#CollectionSidebar')) return; // never the filter rail
      wrapTerms(container, regex);
    });

    if (typeof tippy !== 'function') return;

    var refs = Array.prototype.slice.call(document.querySelectorAll('.bf-term'))
      .filter(function (el) { return !el._tippy && !el.closest('#CollectionSidebar'); });
    if (!refs.length) return;

    tippy(refs, {
      content: buildContent,
      allowHTML: true,
      interactive: true,
      interactiveBorder: 12,
      appendTo: 'parent',                 // the .bf-term-wrap: the pop-up follows its term in the DOM
      popperOptions: { strategy: 'fixed' }, // so a clipping (overflow: hidden) ancestor cannot cut it off
      trigger: 'mouseenter focus click',
      hideOnClick: true,
      aria: { content: 'describedby', expanded: true },
      animation: 'shift-away',
      theme: 'light-border',
      onCreate: hooks.onCreate,
      onShow: hooks.onShow,
      onHide: hooks.onHide
    });
  }

  window.initGlossary = initGlossary; // bf-ingredient-grid-filters.js calls this once the data is in
})();
